import {
    get,
    set,
    merge,
    isEmpty,
    countBy,
    cloneDeep,
} from 'lodash';

import { errors } from '@paychex/core';
import { Fetch, Message } from '@paychex/core/types/data';

export interface RestError {
    code: string,
    resolution: string[],
}

const { error, fatal, ERROR, FATAL } = errors;

function asMessage(e: RestError): Message {
    return {
        code: e.code,
        severity: get(e, 'severity', ERROR),
        data: [get(e, 'resolution')].filter(Boolean)
    };
}

function withUnwrapResponse(fetch: Fetch): Fetch {
    return async function unwrapResponse(request, ...args) {
        const response = cloneDeep(await fetch(request, ...args));

        if (get(request, 'ignore.unwrap')) {
            return response;
        }

        const messages = get(response, 'data.errors', []).map(asMessage);
        merge(response.meta, get(response, 'data.metadata'));
        response.data = get(response, 'data.content', response.data);
        if (!isEmpty(messages)) {
            set(response, 'meta.messages', messages);
            const severities = countBy(messages, 'severity');
            const data = { response };
            if (severities[FATAL]) {
                set(response, 'meta.error', true);
                throw error('REST operation contained critical errors.', fatal(data));
            }
        }
        return response;
    } as Fetch;
}

/**
 * Wraps the data pipeline to provide functionality specific to {@link https://wiki.paychex.com/display/ENTARCH/ReST+API+Standard Paychex REST endpoints},
 * including automatically unwrapping the standard REST response and handling errors and content.
 *
 * To bypass this functionality set the Request's `ignore.unwrap` property to `true`.
 *
 * @param fetch The fetch request to wrap with Paychex REST functionality.
 * @returns A fetch method that handles Paychex REST endpoints.
 */
export function withREST(fetch: Fetch): Fetch {
    return withUnwrapResponse(fetch);
}
